import React from "react";
import {
  FaEnvelope,
  FaFacebook,
  FaGlobe,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhone,
  FaUserTie,
} from "react-icons/fa";

import {
  DEFAULT_COVER,
  DEFAULT_LOGO,
  resolveMediaUrl,
  stripHtml,
} from "../utils/sectionProfileUtils";

export default function SectionPreviewCard({
  formData,
  completion,
  publicLinksCount,
}) {
  const cover = resolveMediaUrl(formData.photoURL, DEFAULT_COVER);
  const logo = resolveMediaUrl(formData.logo_section, DEFAULT_LOGO);
  const aboutText = stripHtml(formData.about_content);

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="relative h-44 overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img
          src={cover}
          alt={formData.nom_section || "Aperçu association"}
          className="h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.src = DEFAULT_COVER;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />

        <div className="absolute bottom-4 left-4 right-4 flex items-end gap-3">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-3xl border border-white/40 bg-white p-2 shadow-xl">
            <img
              src={logo}
              alt={formData.nom_section || "Logo"}
              className="h-full w-full object-contain"
              onError={(event) => {
                event.currentTarget.src = DEFAULT_LOGO;
              }}
            />
          </div>

          <div className="min-w-0 text-white">
            <p className="truncate text-xl font-bold">
              {formData.nom_section || "Nom de l’association"}
            </p>

            <p className="truncate text-sm text-white/75">
              {formData.discipline_section || "Discipline / activité"}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5 p-4 sm:p-5">
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Complétude de la fiche
            </p>

            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
              {completion.percent}%
            </span>
          </div>

          <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${completion.percent}%` }}
            />
          </div>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {completion.completed} champ{completion.completed > 1 ? "s" : ""}{" "}
            renseigné{completion.completed > 1 ? "s" : ""} sur{" "}
            {completion.total}.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <InfoLine
            icon={<FaUserTie />}
            label="Responsable"
            value={formData.responsable_section}
          />
          <InfoLine
            icon={<FaPhone />}
            label="Téléphone"
            value={formData.tel_section}
          />
          <InfoLine
            icon={<FaEnvelope />}
            label="Email"
            value={formData.mail_section}
          />
          <InfoLine
            icon={<FaMapMarkerAlt />}
            label="Adresse"
            value={formData.adresse_section}
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            Présentation
          </p>

          <p className="mt-2 line-clamp-5 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {aboutText || "Aucun texte de présentation renseigné."}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <SocialBadge
            icon={<FaGlobe />}
            active={Boolean(formData.siteweb_section)}
            label="Site web"
          />
          <SocialBadge
            icon={<FaFacebook />}
            active={Boolean(formData.facebook_section)}
            label="Facebook"
          />
          <SocialBadge
            icon={<FaInstagram />}
            active={Boolean(formData.instagram_section)}
            label="Instagram"
          />

          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {publicLinksCount}/3 lien{publicLinksCount > 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

function InfoLine({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-white/10 dark:bg-slate-950">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-primary">{icon}</span>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>

          <p className="mt-0.5 truncate text-sm font-bold text-slate-800 dark:text-slate-100">
            {value || "Non renseigné"}
          </p>
        </div>
      </div>
    </div>
  );
}

function SocialBadge({ icon, label, active }) {
  return (
    <span
      className={[
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1",
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700",
      ].join(" ")}
    >
      {icon}
      {label}
    </span>
  );
}